import axios, { AxiosResponse } from 'axios';
import { SendVerificationCodeMethod } from '@/constants/enums';
import { ServiceResponse } from '@/OptimusRoutes/interfaces';
import { getUniqueSessionId } from '@/utilities/utilities';
import { REFRESH_TOKEN_ENDPOINT } from '../../constants/global';
import { isEmail } from '../../utilities/util';
import { RouteUser } from '../entities/routeUser.entity';

type TwoFactorAuthDto = {
	twoFaEnabled?: boolean;
	twoFaAccessToken?: string;
	recipient?: string;
};

type Auth = TwoFactorAuthDto & {
	accessToken: string;
	ttl: number;
	created?: Date;
	userId: number;
	account: RouteUser;
	userExist?: boolean;
};

type ImpersonateDto = {
	token: Auth;
	refreshToken: string;
	expiresIn: string;
};

type RenewingRefreshTokenDto = {
	refreshToken: string;
	accessToken: string;
	expiresIn: string;
	roles: number[];
};

type RevertImpersonateDto = {
	userId: number;
	impersonator: number;
	refreshToken: number;
};

type ResendTwoFactorDto = {
	method: SendVerificationCodeMethod;
	recipient: string;
};

type TwoFactorAuthStatusDto = {
	enable: boolean;
};

type ChangePasswordDto = {
	oldPassword: string;
	newPassword: string;
};

type AuthServiceType = {
	Login: (auth: Credentials) => Promise<Auth>;
	Logout: (refreshToken: string) => Promise<AxiosResponse<void>>;
	Impersonate: (userId: number) => Promise<AxiosResponse<ImpersonateDto>>;
	RevertImpersonate: (data: RevertImpersonateDto) => Promise<AxiosResponse<void>>;
	UpdateRefreshToken: (oldRefreshToken: string) => Promise<RenewingRefreshTokenDto>;
	verifyTwoFactor: (code: string, token: string) => Promise<ServiceResponse<Auth>>;
	resendTwoFactor: (
		method: SendVerificationCodeMethod,
		token: string
	) => Promise<ServiceResponse<ResendTwoFactorDto>>;
	twoFactorAuthStatus: (data: TwoFactorAuthStatusDto) => Promise<ServiceResponse>;
	forgetPassword: (email: string) => Promise<ServiceResponse>;
	resetPassword: (token: string, password: string, version?: string) => Promise<ServiceResponse>;
	changePassword: (data: ChangePasswordDto) => Promise<ServiceResponse>;
};

type Credentials = {
	username?: string;
	email?: string;
	password: string;
	remember?: boolean;
	ttl?: number;
};

const Login = async (auth: Credentials): Promise<Auth> => {
	let options: Credentials = {
		password: auth.password,
		remember: auth.remember,
	};

	if (isEmail(auth.username)) {
		options.email = auth.username;
	} else {
		options.username = auth.username;
	}
	const sessionId = getUniqueSessionId();

	const { data } = await axios.post<Auth>(`/v3/auth/login`, options, {
		headers: {
			'X-session-id': sessionId,
		},
	});

	return data;
};

const Logout = async (refreshToken: string): Promise<AxiosResponse<void>> =>
	await axios.post(`/v2/auth/logout`, { refreshToken });

const Impersonate = async (userId: number): Promise<AxiosResponse<ImpersonateDto>> =>
	axios.post<ImpersonateDto>(`/v2/auth/impersonate`, { userId });

const RevertImpersonate = async (data: RevertImpersonateDto): Promise<AxiosResponse<void>> =>
	await axios.post(`/v2/auth/impersonate/revert/`, data);

const UpdateRefreshToken = async (oldRefreshToken: string): Promise<RenewingRefreshTokenDto> => {
	const { data } = await axios.post(REFRESH_TOKEN_ENDPOINT, {
		refreshToken: oldRefreshToken,
	});
	return data;
};

const VerifyTwoFactor = async (code: string, token: string): Promise<ServiceResponse<Auth>> => {
	const sessionId = getUniqueSessionId();
	const { data } = await axios.post<ServiceResponse<Auth>>(
		`/v2/auth/2fa/verify`,
		{ code },
		{
			headers: {
				Authorization: `Bearer ${token}`,
				'X-session-id': sessionId,
			},
		}
	);
	return data;
};

const resendTwoFactor = async (
	method: SendVerificationCodeMethod,
	token: string
): Promise<ServiceResponse<ResendTwoFactorDto>> => {
	const { data } = await axios.post<ServiceResponse<ResendTwoFactorDto>>(
		`/v2/auth/2fa/generate`,
		{ method },
		{
			headers: {
				Authorization: `Bearer ${token}`,
			},
		}
	);
	return data;
};

const twoFactorAuthStatus = async (twoFaStatus: TwoFactorAuthStatusDto): Promise<ServiceResponse> => {
	const { data } = await axios.post<ServiceResponse>(`/v2/auth/2fa/status`, twoFaStatus);
	return data;
};

const forgetPassword = async (email: string): Promise<ServiceResponse> => {
	const { data } = await axios.post<ServiceResponse>(`/v2/auth/forgot-password`, { email });
	return data;
};

const resetPassword = async (token: string, password: string, version?: string): Promise<ServiceResponse> => {
	if (version === '1') {
		const { data } = await axios.post<ServiceResponse>(
			'Accounts/reset-password',
			{ newPassword: password },
			{
				params: {
					access_token: token,
				},
			}
		);

		if (data?.error) {
			return data;
		}

		return data ?? {};
	}

	const { data } = await axios.post<ServiceResponse>(
		`/v2/auth/reset-password`,
		{ password },
		{
			headers: {
				Authorization: `Bearer ${token}`,
			},
		}
	);
	return data;
};

const changePassword = async (data: ChangePasswordDto): Promise<ServiceResponse> => {
	const { data: response } = await axios.post<ServiceResponse>('/v2/auth/change-password', data);
	return response;
};

const AuthService: AuthServiceType = {
	Login,
	Logout,
	Impersonate,
	RevertImpersonate,
	UpdateRefreshToken,
	verifyTwoFactor: VerifyTwoFactor,
	resendTwoFactor,
	twoFactorAuthStatus,
	forgetPassword,
	resetPassword,
	changePassword,
};

export default AuthService;
